'use client';

import { useLanguage } from '@bells/core/i18n';
import { cn } from '@bells/core/lib/utils';
import {
  cookieConsentAccept,
  cookieConsentAcceptDescription,
  cookieConsentDecline,
  cookieConsentDescription,
  cookieConsentReadMore,
  cookieConsentTitle,
} from '@bells/i18n';
import { baseLocale } from '@bells/i18n/runtime';
import { CookieIcon } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useLCP } from '../utils/use-lcp';
import { Button } from './button';

declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}

export default function CookieConsent({
  isHidden,
  cookieName,
}: {
  isHidden?: boolean;
  cookieName?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [hide, setHide] = useState(isHidden);
  const { language } = useLanguage();
  const [afterLcp, setAfterLcp] = useState(false);

  useLCP(() => {
    setAfterLcp(true);
  });

  const accept = () => {
    document.cookie = `${cookieName}=true; expires=Fri, 31 Dec 9999 23:59:59 GMT; path=/`;
    setHide(true);
    loadTrackingScripts();
  };

  const decline = () => {
    setHide(true);
  };

  useEffect(() => {
    if (afterLcp && !isHidden) {
      setIsOpen(true);
    }
  }, [afterLcp]);

  function loadTrackingScripts() {
    window.gtag?.('consent', 'update', {
      ad_storage: 'granted',
      analytics_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
    });
  }

  useEffect(() => {
    const cookies = document.cookie.split(';').map((c) => c.trim());
    const consent = cookies.find((c) => c.startsWith('cookieConsent='));

    const isActive = consent?.endsWith('true') === true;

    if (isActive) {
      loadTrackingScripts();
    }
  }, []);

  if (hide) {
    return null;
  }

  return (
    <div
      className={cn(
        'pointer-events-none fixed bottom-0 left-0 right-0 z-[0] w-full select-none opacity-0 transition-[opacity,transform] delay-0 duration-300 sm:left-auto sm:right-4 sm:max-w-md sm:pb-4',
        !isOpen
          ? 'translate-y-[100%]'
          : 'pointer-events-auto z-[200] translate-y-0 select-auto opacity-100'
      )}
    >
      <div className='m-3 rounded-md border border-border bg-background shadow-lg dark:bg-[#111]'>
        <div className='grid gap-2'>
          <div className='flex h-14 items-center justify-between border-b border-border p-4'>
            <div className='text-lg font-medium'>{cookieConsentTitle()}</div>
            <CookieIcon className='h-[1.2rem] w-[1.2rem]' />
          </div>
          <div className='p-4'>
            <p className='text-start text-sm font-normal'>
              {cookieConsentDescription()}
              <br />
              <br />
              <span className='text-xs'>
                {cookieConsentAcceptDescription()}
              </span>
              <br />
              <Link
                href={
                  language === baseLocale
                    ? `/datenschutz`
                    : `/${language}/privacy`
                }
                className='text-xs underline'
              >
                {cookieConsentReadMore()}
              </Link>
            </p>
          </div>
          <div className='flex gap-2 border-t border-border p-4 py-5 dark:bg-background/20'>
            <Button onClick={accept} className='w-full'>
              {cookieConsentAccept()}
            </Button>
            <Button onClick={decline} className='w-full' variant='outline'>
              {cookieConsentDecline()}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
